import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiCall } from '../../lib/api.js';
import { useAccessToken, requireToken } from '../../lib/use-access-token.js';

export type ModuleId = 'evl' | 'offer' | 'termination';

export interface IntegrationRoleRequirement {
  provider: string;
  role: string;
  description: string;
  optional?: boolean;
}

export interface RoleBindingStatus {
  role: string;
  provider: string;
  description: string;
  optional: boolean;
  connectionName: string;
  connectionStatus: 'unconfigured' | 'connected' | 'failing' | null;
}

export interface ModuleDetail {
  id: ModuleId;
  title: string;
  description: string;
  registryVersion: number;
  requiresIntegrations: IntegrationRoleRequirement[];
  storedVersion: number | null;
  enabled: boolean;
  config: unknown | null;
  integrationBindings: Record<string, string>;
  roleBindings: RoleBindingStatus[];
  configuredBy: string | null;
  configuredAt: string | null;
  unmetRoles: string[];
  canEnable: boolean;
  upgradeAvailable: boolean;
}

export function useModules(companyId: string | undefined) {
  const { token } = useAccessToken();
  return useQuery<{ items: ModuleDetail[] }, Error>({
    queryKey: ['modules', companyId, token],
    enabled: !!companyId && !!token,
    queryFn: async ({ signal }) =>
      apiCall(`/v1/companies/${companyId}/modules`, { token: requireToken(token), signal }),
    staleTime: 15_000,
  });
}

export function useModule(companyId: string | undefined, moduleId: ModuleId | undefined) {
  const { token } = useAccessToken();
  return useQuery<ModuleDetail, Error>({
    queryKey: ['module', companyId, moduleId, token],
    enabled: !!companyId && !!moduleId && !!token,
    queryFn: async ({ signal }) =>
      apiCall(`/v1/companies/${companyId}/modules/${moduleId}`, {
        token: requireToken(token),
        signal,
      }),
    staleTime: 15_000,
  });
}

export interface SaveModuleInput {
  config: unknown;
  integrationBindings?: Record<string, string>;
}

export function useSaveModule(companyId: string, moduleId: ModuleId) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<void, Error, SaveModuleInput>({
    mutationFn: async (input) => {
      await apiCall(`/v1/companies/${companyId}/modules/${moduleId}`, {
        method: 'PUT',
        token: requireToken(token),
        body: input,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['module', companyId, moduleId] });
      void qc.invalidateQueries({ queryKey: ['modules', companyId] });
    },
  });
}

export function useEnableModule(companyId: string, moduleId: ModuleId) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<void, Error>({
    mutationFn: async () => {
      await apiCall(`/v1/companies/${companyId}/modules/${moduleId}/enable`, {
        method: 'POST',
        token: requireToken(token),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['module', companyId, moduleId] });
      void qc.invalidateQueries({ queryKey: ['modules', companyId] });
    },
  });
}

export function useDisableModule(companyId: string, moduleId: ModuleId) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<void, Error>({
    mutationFn: async () => {
      await apiCall(`/v1/companies/${companyId}/modules/${moduleId}/disable`, {
        method: 'POST',
        token: requireToken(token),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['module', companyId, moduleId] });
      void qc.invalidateQueries({ queryKey: ['modules', companyId] });
    },
  });
}
